import React, {
  useState, useCallback, forwardRef, useImperativeHandle, useRef, useEffect,
} from 'react';

import {
  Form, Button, notification, Select,
} from 'antd';
import * as Yup from 'yup';

import useManager from '../../../hooks/manager';
import api from '../../../services/api';
import Modal from '../../Modal';

const NewForm = ({ onSuccess }, ref) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [matrizCompany, setMatrizCompany] = useState(null);
  const [filiais, setFiliais] = useState([]);
  const { store } = useManager();
  const formRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/company');
        setCompanies(data);
      } catch (err) {
        console.error('Failed to load companies', err);
      }
    })();
  }, []);

  useImperativeHandle(ref, () => ({
    open: () => {
      setMatrizCompany(null);
      setFiliais([]);
      setValidationErrors({});
      setShowModal(true);
    },
  }));

  const filialOptions = companies.filter((c) => String(c._id) !== String(matrizCompany));

  const handleSubmit = useCallback(() => {
    (async () => {
      try {
        setLoading(true);
        const data = {
          matrizCompany,
          filiais,
        };

        const schema = Yup.object().shape({
          matrizCompany: Yup.string().required('Selecione a empresa matriz'),
          filiais: Yup.array().min(1, 'Selecione ao menos uma filial').required(),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        store('/matriz-filial', data, () => {
          setMatrizCompany(null);
          setFiliais([]);
          setValidationErrors({});
          setShowModal(false);
          if (onSuccess) {
            onSuccess();
          }
        });
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log(err);
        setValidationErrors({});
        if (err instanceof Yup.ValidationError) {
          notification.error({
            message: 'Falha na Validação',
          });
          const ve = {};
          err.inner.forEach((e) => {
            ve[e.path] = e.message;
          });
          setValidationErrors(ve);
          return;
        }

        if (err && err.response && err.response.data) {
          notification.error({
            message: err.response.data.message,
          });
          return;
        }

        notification.error({
          message: 'Falha ao cadastrar',
        });
      }
    })();
  }, [store, matrizCompany, filiais, onSuccess]);

  return (
    <Modal
      title="Cadastrar Matriz e Filial"
      visible={showModal}
      loading={loading}
      footer={(
        <Button type="primary" size="large" onClick={handleSubmit}>
          Cadastrar
        </Button>
      )}
      onClose={() => setShowModal(false)}
    >
      <Form ref={formRef} size="large">
        <Form.Item
          validateStatus={validationErrors.matrizCompany ? 'error' : ''}
          help={validationErrors.matrizCompany}
          hasFeedback
        >
          <Select
            placeholder="Empresa matriz"
            value={matrizCompany}
            onChange={(value) => {
              setMatrizCompany(value);
              setFiliais((prev) => prev.filter((id) => String(id) !== String(value)));
            }}
            showSearch
            optionFilterProp="children"
          >
            {companies.map((c) => (
              <Select.Option key={c._id} value={c._id}>
                {c.fantasyName}
                {' - '}
                {c.name}
                {c.cnpj ? ` (${c.cnpj})` : ''}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          validateStatus={validationErrors.filiais ? 'error' : ''}
          help={validationErrors.filiais}
          hasFeedback
        >
          <Select
            mode="multiple"
            placeholder="Empresas filiais"
            value={filiais}
            onChange={setFiliais}
            showSearch
            optionFilterProp="children"
            disabled={!matrizCompany}
          >
            {filialOptions.map((c) => (
              <Select.Option key={c._id} value={c._id}>
                {c.fantasyName}
                {' - '}
                {c.name}
                {c.cnpj ? ` (${c.cnpj})` : ''}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default forwardRef(NewForm);
